import React, { useEffect, useMemo, useState } from 'react';
import { Bin, Shelf, Tray, Warehouse, binsAPI, shelvesAPI, traysAPI, warehousesAPI } from '../../services/api';

interface InventoryLocationPickerProps {
  trayId: string;
  onTrayIdChange: (trayId: string) => void;
  initialTray?: Tray | null;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

const InventoryLocationPicker: React.FC<InventoryLocationPickerProps> = ({
  trayId,
  onTrayIdChange,
  initialTray,
  disabled,
  required,
  className = '',
}) => {
  const [warehouseId, setWarehouseId] = useState('');
  const [shelfId, setShelfId] = useState('');
  const [binId, setBinId] = useState('');
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [shelves, setShelves] = useState<Shelf[]>([]);
  const [bins, setBins] = useState<Bin[]>([]);
  const [trays, setTrays] = useState<Tray[]>([]);
  const [loading, setLoading] = useState({
    warehouses: false,
    shelves: false,
    bins: false,
    trays: false,
  });

  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        setLoading((current) => ({ ...current, warehouses: true }));
        setWarehouses(await warehousesAPI.getAll());
      } catch (error) {
        console.error('Failed to load warehouses:', error);
      } finally {
        setLoading((current) => ({ ...current, warehouses: false }));
      }
    };

    loadWarehouses();
  }, []);

  useEffect(() => {
    if (!initialTray) return;

    setWarehouseId(initialTray.bin?.shelf?.warehouse?.id?.toString() || '');
    setShelfId(initialTray.bin?.shelf?.id?.toString() || '');
    setBinId(initialTray.bin?.id?.toString() || '');
  }, [initialTray?.id]);

  useEffect(() => {
    if (!warehouseId) {
      setShelves([]);
      return;
    }

    const loadShelves = async () => {
      try {
        setLoading((current) => ({ ...current, shelves: true }));
        setShelves(await shelvesAPI.getAll(Number(warehouseId)));
      } catch (error) {
        console.error('Failed to load shelves:', error);
      } finally {
        setLoading((current) => ({ ...current, shelves: false }));
      }
    };

    loadShelves();
  }, [warehouseId]);

  useEffect(() => {
    if (!shelfId) {
      setBins([]);
      return;
    }

    const loadBins = async () => {
      try {
        setLoading((current) => ({ ...current, bins: true }));
        setBins(await binsAPI.getAll(Number(shelfId)));
      } catch (error) {
        console.error('Failed to load bins:', error);
      } finally {
        setLoading((current) => ({ ...current, bins: false }));
      }
    };

    loadBins();
  }, [shelfId]);

  useEffect(() => {
    if (!binId) {
      setTrays([]);
      return;
    }

    const loadTrays = async () => {
      try {
        setLoading((current) => ({ ...current, trays: true }));
        setTrays(await traysAPI.getAll(Number(binId)));
      } catch (error) {
        console.error('Failed to load trays:', error);
      } finally {
        setLoading((current) => ({ ...current, trays: false }));
      }
    };

    loadTrays();
  }, [binId]);

  const selectedPath = useMemo(() => {
    if (!trayId) return '';

    const selectedTray = trays.find((tray) => tray.id === Number(trayId))
      || (initialTray?.id === Number(trayId) ? initialTray : null);
    if (!selectedTray) return '';

    return [
      selectedTray.bin?.shelf?.warehouse?.name,
      selectedTray.bin?.shelf?.code,
      selectedTray.bin?.code,
      selectedTray.code,
    ].filter(Boolean).join(' - ');
  }, [initialTray, trayId, trays]);

  const handleWarehouseChange = (value: string) => {
    setWarehouseId(value);
    setShelfId('');
    setBinId('');
    setTrays([]);
    setBins([]);
    onTrayIdChange('');
  };

  const handleShelfChange = (value: string) => {
    setShelfId(value);
    setBinId('');
    setTrays([]);
    onTrayIdChange('');
  };

  const handleBinChange = (value: string) => {
    setBinId(value);
    onTrayIdChange('');
  };

  return (
    <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 ${className}`}>
      <div>
        <label className="block text-xs font-medium text-neutral-600">Warehouse{required ? ' *' : ''}</label>
        <select
          value={warehouseId}
          onChange={(event) => handleWarehouseChange(event.target.value)}
          disabled={disabled || loading.warehouses}
          className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:opacity-60"
        >
          <option value="">{loading.warehouses ? 'Loading warehouses...' : 'Select warehouse'}</option>
          {warehouses.map((warehouse) => (
            <option key={warehouse.id} value={warehouse.id}>{warehouse.name}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs font-medium text-neutral-600">Shelf{required ? ' *' : ''}</label>
        <select
          value={shelfId}
          onChange={(event) => handleShelfChange(event.target.value)}
          disabled={disabled || !warehouseId || loading.shelves}
          className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:opacity-60"
        >
          <option value="">{loading.shelves ? 'Loading shelves...' : 'Select shelf'}</option>
          {shelves.map((shelf) => (
            <option key={shelf.id} value={shelf.id}>{shelf.code}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs font-medium text-neutral-600">Bin{required ? ' *' : ''}</label>
        <select
          value={binId}
          onChange={(event) => handleBinChange(event.target.value)}
          disabled={disabled || !shelfId || loading.bins}
          className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:opacity-60"
        >
          <option value="">{loading.bins ? 'Loading bins...' : 'Select bin'}</option>
          {bins.map((bin) => (
            <option key={bin.id} value={bin.id}>{bin.code}</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs font-medium text-neutral-600">Tray{required ? ' *' : ''}</label>
        <select
          value={trayId}
          onChange={(event) => onTrayIdChange(event.target.value)}
          disabled={disabled || !binId || loading.trays}
          required={required}
          className="mt-1 block w-full rounded-md border-neutral-300 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500 disabled:opacity-60"
        >
          <option value="">{loading.trays ? 'Loading trays...' : 'Select tray'}</option>
          {trays.map((tray) => (
            <option key={tray.id} value={tray.id}>{tray.code}</option>
          ))}
        </select>
        {selectedPath && (
          <p className="mt-1 truncate text-xs text-neutral-500">{selectedPath}</p>
        )}
      </div>
    </div>
  );
};

export default InventoryLocationPicker;
